import React, { useState, Children } from 'react';
import { Link } from 'react-router-dom';
import { GlobeIcon, MailIcon, InstagramIcon, TwitterIcon, LightbulbIcon, HeartIcon, QuoteIcon, AwardIcon, GiftIcon, ChevronDownIcon, TagIcon, UsersIcon, AlertCircleIcon, FlameIcon, MoonIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import HeatLevelIndicator from '../../components/UI/HeatLevelIndicator';
import DarkLevelIndicator from '../../components/UI/DarkLevelIndicator';
import { ImageCarousel } from './ImageCarousel';
import { authorData } from './data';
const donationData = {
  isPublic: true,
  totalDonated: 1247.5,
  causes: [{
    id: '1',
    name: "Children's Literacy Foundation",
    slug: 'childrens-literacy-foundation',
    readerTotal: 3842.0
  }, {
    id: '2',
    name: 'Book Access for All',
    slug: 'book-access-for-all',
    readerTotal: 2156.5
  }, {
    id: '3',
    name: 'Libraries First',
    slug: 'libraries-first',
    readerTotal: 1290.0
  }]
};
export const AboutMeTab = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const [givingBackOpen, setGivingBackOpen] = useState(true);
  const hasAestheticImages = authorData.aestheticImages && authorData.aestheticImages.length > 0;
  const isLandscapeLayout = authorData.profileLayout === 'landscape';
  return <div className="space-y-8" data-id="element-6310" data-mp="3goknvcav9v1o">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-6311" data-mp="3groz2tmr1r1u">
        {/* Left Column — What You'll Find in My Books */}
        <div className="lg:col-span-1" data-id="element-6312" data-mp="36zsz1d13rquz">
          <div className={`rounded-lg border overflow-hidden shadow-sm ${dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-6313" data-mp="36yremhy75s0l">
            {/* Card Header */}
            <div className={`px-6 py-4 border-b ${dk.isDark ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-6314" data-mp="3bhlfrscuq1j">
              <h3 className={`text-lg font-medium ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6315" data-mp="d4ls0fzvezfl">
                What You'll Find in My Books
              </h3>
            </div>

            {/* Aesthetic Images — portrait layout only */}
            {hasAestheticImages && !isLandscapeLayout && <div className={`border-b ${dk.isDark ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-6316" data-mp="3qcyeb5mne1qw">
                <div className="p-4" data-id="element-6317" data-mp="3llgkd1j1l1ff">
                  <ImageCarousel images={authorData.aestheticImages} data-id="element-6318" data-mp="3lkez648bayjz" />
                </div>
              </div>}

            {/* Content Type */}
            <div className={`px-6 py-4 border-b ${dk.isDark ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-6319" data-mp="3cmuuelb8qk4m">
              <div className="flex items-center mb-3" data-id="element-6320" data-mp="37lyvyn9nrq6l">
                <TagIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" data-id="element-6321" data-mp="32ndrucracmcv" />
                <span className={`text-sm font-medium ${dk.isDark ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-6322" data-mp="1i5v2bpftujyt">
                  Content Type
                </span>
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-6323" data-mp="1s3izsn4ti5ko">
                {authorData.badges.contentType.map(type => <span key={type} className={`px-3 py-1 ${rt.badgeRadius} text-xs ${dk.isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'}`} data-id="element-6324" data-mp="1w42yhv51hh8x">
                    {type}
                  </span>)}
              </div>
            </div>

            {/* Spice & Darkness */}
            <div className={`px-6 py-4 border-b ${dk.isDark ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-6325" data-mp="1d71qs2k3p31a">
              <div className="flex items-center mb-3" data-id="element-6326" data-mp="gnuee077pelz">
                <FlameIcon className="h-4 w-4 text-orange-500 mr-2 flex-shrink-0" data-id="element-6327" data-mp="kpz4ioygbt3v" />
                <span className={`text-sm font-medium ${dk.isDark ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-6328" data-mp="knutzo61rvum">
                  Spice & Darkness
                </span>
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-6329" data-mp="bb5i2xeh12yt">
                <HeatLevelIndicator level={authorData.badges.spiceLevel} darkMode={dk.isDark} data-id="element-6330" data-mp="bcq9ie5ho5sg" />
                <DarkLevelIndicator level={(authorData.badges as any).darknessLevel} darkMode={dk.isDark} data-id="element-6331" data-mp="kbkeoswvy4hm" />
              </div>
            </div>

            {/* Representation */}
            <div className={`px-6 py-4 border-b ${dk.isDark ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-6332" data-mp="u8yklyl6navo">
              <div className="flex items-center mb-3" data-id="element-6333" data-mp="p93q7l27835v">
                <UsersIcon className="h-4 w-4 text-indigo-500 mr-2 flex-shrink-0" data-id="element-6334" data-mp="2w2o02fqkwqur" />
                <span className={`text-sm font-medium ${dk.isDark ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-6335" data-mp="2r335simct6od">
                  Representation
                </span>
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-6336" data-mp="1bikbfb67immn">
                {authorData.badges.representation.map(rep => <span key={rep} className={`px-3 py-1 ${rt.badgeRadius} text-xs ${dk.isDark ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-100 text-indigo-800'}`} data-id="element-6337" data-mp="wu67ewx20s4i">
                    {rep}
                  </span>)}
              </div>
            </div>

            {/* Content Warnings */}
            <div className={`px-6 py-4 border-b ${dk.isDark ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-6338" data-mp="dwvc1nejmiuc">
              <div className="flex items-center mb-3" data-id="element-6339" data-mp="iv6n21gkqze5">
                <AlertCircleIcon className="h-4 w-4 text-amber-500 mr-2 flex-shrink-0" data-id="element-6340" data-mp="289l6c68tpu29" />
                <span className={`text-sm font-medium ${dk.isDark ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-6341" data-mp="2d9hb47pchpiv">
                  Content Warnings
                </span>
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-6342" data-mp="26sfpclpidngy">
                {authorData.badges.triggers.map(trigger => <span key={trigger} className={`px-3 py-1 ${rt.badgeRadius} text-xs ${dk.isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-800'}`} data-id="element-6343" data-mp="21ue1lnh50zz5">
                    {trigger}
                  </span>)}
              </div>
            </div>

            {/* Social Links */}
            <div className={`px-6 py-4 ${dk.isDark ? 'bg-gray-900/30' : 'bg-gray-50'}`} data-id="element-6344" data-mp="2krowywznf99b">
              <p className={`text-xs font-medium uppercase tracking-wider mb-3 ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6345" data-mp="2fsc866p7vs43">
                Connect With Me
              </p>
              <div className="flex gap-4" data-id="element-6346" data-mp="38ghx75nb039q">
                <a href={authorData.social.website} className={`transition-colors ${dk.isDark ? 'text-gray-400 hover:text-indigo-400' : 'text-gray-500 hover:text-indigo-600'}`} aria-label="Website" data-id="element-6347" data-mp="3gqp1ftva7olh">
                  <GlobeIcon className="h-5 w-5" data-id="element-6348" data-mp="3pq2cus2yun8u" />
                </a>
                <a href={authorData.social.instagram} className={`transition-colors ${dk.isDark ? 'text-gray-400 hover:text-indigo-400' : 'text-gray-500 hover:text-indigo-600'}`} aria-label="Instagram" data-id="element-6349" data-mp="3ur9iakhmzacc">
                  <InstagramIcon className="h-5 w-5" data-id="element-6350" data-mp="cbyzly79i342" />
                </a>
                <a href={authorData.social.twitter} className={`transition-colors ${dk.isDark ? 'text-gray-400 hover:text-indigo-400' : 'text-gray-500 hover:text-indigo-600'}`} aria-label="Twitter" data-id="element-6351" data-mp="hbk8p1g5brui">
                  <TwitterIcon className="h-5 w-5" data-id="element-6352" data-mp="r9zq2cpdfmqt" />
                </a>
                <a href={authorData.social.newsletter} className={`transition-colors ${dk.isDark ? 'text-gray-400 hover:text-indigo-400' : 'text-gray-500 hover:text-indigo-600'}`} aria-label="Newsletter" data-id="element-6353" data-mp="1cjn7gfpz7hil">
                  <MailIcon className="h-5 w-5" data-id="element-6354" data-mp="1glrxz5l4ny10" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="lg:col-span-2 space-y-6" data-id="element-6355" data-mp="sume8fc2gllf">
          {/* Giving Back — collapsible, above aesthetics */}
          {donationData.isPublic && <div className={`rounded-lg border overflow-hidden shadow-sm ${dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-6356" data-mp="12nk3r5fh7atj">
              <button onClick={() => setGivingBackOpen(!givingBackOpen)} className={`w-full px-6 py-4 flex items-center justify-between transition-colors ${dk.isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`} data-id="element-6357" data-mp="a5plwh08c35u">
                <div className="flex items-center gap-3" data-id="element-6358" data-mp="2gsdhuamise30">
                  <div className={`p-1.5 rounded-full ${dk.isDark ? 'bg-rose-900/30' : 'bg-rose-100'}`} data-id="element-6359" data-mp="2lm87rvspxpaj">
                    <GiftIcon className="h-4 w-4 text-rose-500" data-id="element-6360" data-mp="27ryt11ackgur" />
                  </div>
                  <div className="text-left" data-id="element-6361" data-mp="2cqs9n2kxknz5">
                    <span className={`block text-base font-medium ${dk.isDark ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-6362" data-mp="30i7fh5bx1nf5">
                      Giving Back
                    </span>
                    <span className={`text-sm ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6363" data-mp="2qjhuh3oq3muo">
                      $
                      {donationData.totalDonated.toLocaleString('en-US', {
                    minimumFractionDigits: 2
                  })}{' '}
                      donated to date
                    </span>
                  </div>
                </div>
                <ChevronDownIcon className={`h-5 w-5 transition-transform duration-200 ${givingBackOpen ? 'rotate-180' : ''} ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6364" data-mp="rm0ilmu1od7i" />
              </button>

              <div className={`transition-all duration-200 overflow-hidden ${givingBackOpen ? 'max-h-[500px]' : 'max-h-0'}`} data-id="element-6365" data-mp="msnwbjcu8ihx">
                <div className={`px-6 pb-6 border-t ${dk.isDark ? 'border-gray-700' : 'border-gray-100'}`} data-id="element-6366" data-mp="8s4tcqn8c2mm">
                  <p className={`text-sm font-medium mt-4 mb-3 ${dk.isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-6367" data-mp="3v15xyn6fnrgm">
                    Supported Causes
                  </p>
                  <div className="space-y-3" data-id="element-6368" data-mp="1t8zuvljko2e1">
                    {donationData.causes.map(cause => <Link key={cause.id} to={`/donations/nonprofit/${cause.slug}`} className={`flex items-center justify-between p-3 rounded-xl transition-colors ${dk.isDark ? 'bg-gray-700/50 hover:bg-gray-700' : 'bg-gray-50 hover:bg-gray-100'}`} data-id="element-6369" data-mp="1y7b8m7czmx1j">
                        <div className="flex items-center gap-2 min-w-0" data-id="element-6370" data-mp="3si19cw5vb9nl">
                          <HeartIcon className={`h-4 w-4 flex-shrink-0 ${dk.isDark ? 'text-rose-400' : 'text-rose-500'}`} data-id="element-6371" data-mp="3nnwxbyhqwbfn" />
                          <span className={`text-sm font-medium truncate ${dk.isDark ? 'text-gray-200' : 'text-gray-800'}`} data-id="element-6372" data-mp="bdy1g1vf888">
                            {cause.name}
                          </span>
                        </div>
                        <div className="flex flex-col items-end ml-3 flex-shrink-0" data-id="element-6373" data-mp="2h09lgppxax3a">
                          <span className={`text-xs font-semibold ${dk.isDark ? 'text-rose-400' : 'text-rose-600'}`} data-id="element-6374" data-mp="2c2854cjvtbvi">
                            $
                            {cause.readerTotal.toLocaleString('en-US', {
                        minimumFractionDigits: 2
                      })}
                          </span>
                          <span className={`text-[10px] ${dk.isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-6375" data-mp="3fplmybthcghp">
                            from readers
                          </span>
                        </div>
                      </Link>)}
                  </div>
                  <p className={`text-xs mt-4 ${dk.isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-6376" data-mp="3tikxdzeenkie">
                    5% of every sale goes to these causes ❤️
                  </p>
                </div>
              </div>
            </div>}

          {/* Aesthetics / Image Carousel — landscape layout */}
          {hasAestheticImages && isLandscapeLayout && <div className={`border rounded-2xl p-6 shadow-sm ${dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-6377" data-mp="1wsi9kzzfl0ta">
              <ImageCarousel images={authorData.aestheticImages} data-id="element-6378" data-mp="1ms7xxjt1o1g8" />
            </div>}

          {/* Writing Inspiration */}
          <div className={`border rounded-2xl p-6 shadow-sm ${dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-6379" data-mp="1hvr7uk2abd0i">
            <div className="flex items-center mb-4" data-id="element-6380" data-mp="2aibz7kirfp42">
              <LightbulbIcon className="h-5 w-5 text-yellow-500 mr-2" data-id="element-6381" data-mp="2aky4tem3bujn" />
              <h3 className={`text-xl font-semibold ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6382" data-mp="25qaow455k8go">
                Writing Inspiration
              </h3>
            </div>
            <div className="space-y-4" data-id="element-6383" data-mp="2y9r7rv14r2nr">
              <p className={dk.isDark ? 'text-gray-300' : 'text-gray-700'} data-id="element-6384" data-mp="2thpwocfp7hzb">
                Authors who inspire my work:
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-6385" data-mp="2dyn8o7924nij">
                {authorData.writingInspiration.map(name => <span key={name} className={`px-3 py-1 rounded-full text-sm ${dk.isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'}`} data-id="element-6386" data-mp="2izugu7f43opq">
                    {name}
                  </span>)}
              </div>
              <p className={`mt-4 ${dk.isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-6387" data-mp="2wqprnp968fvy">
                Favorite books that shaped my writing style:
              </p>
              <ul className={`list-disc pl-5 ${dk.isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-6388" data-mp="2mzuuvqyatxw9">
                {authorData.favoriteBooks.map(book => <li key={book} data-id="element-6389" data-mp="3fn8w41915fm6">{book}</li>)}
              </ul>
            </div>
          </div>

          {/* Favorite Tropes */}
          <div className={`border rounded-2xl p-6 shadow-sm ${dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-6390" data-mp="1909v4it7plm6">
            <div className="flex items-center mb-4" data-id="element-6391" data-mp="1416paw5erbd5">
              <HeartIcon className="h-5 w-5 text-pink-500 mr-2" data-id="element-6392" data-mp="1iqupfu1us3za" />
              <h3 className={`text-xl font-semibold ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6393" data-mp="1is5sfrnh57pc">
                Favorite Tropes
              </h3>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-6394" data-mp="q3iekh521klt">
              {authorData.favoriteTropes.map(trope => <span key={trope} className={`px-3 py-1 rounded-full text-sm ${dk.isDark ? 'bg-pink-900/30 text-pink-300' : 'bg-pink-50 text-pink-700'}`} data-id="element-6395" data-mp="z2ditnojgkmh">
                  {trope}
                </span>)}
            </div>
          </div>

          {/* Writing Process */}
          <div className={`border rounded-2xl p-6 shadow-sm ${dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-6396" data-mp="26evx70r7jgdn">
            <div className="flex items-center mb-4" data-id="element-6397" data-mp="183umequvunsp">
              <QuoteIcon className={`h-5 w-5 mr-2 ${dk.isDark ? 'text-indigo-400' : 'text-indigo-500'}`} data-id="element-6398" data-mp="182jjet99hk2n" />
              <h3 className={`text-xl font-semibold ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6399" data-mp="1h4sqta98gxbt">
                My Writing Process
              </h3>
            </div>
            <p className={dk.isDark ? 'text-gray-300' : 'text-gray-700'} data-id="element-6400" data-mp="pdmqottiizmj">
              {authorData.writingProcess}
            </p>
          </div>

          {/* Fun Facts */}
          <div className={`border rounded-2xl p-6 shadow-sm ${dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-6401" data-mp="td3w5n8xvwrp">
            <div className="flex items-center mb-4" data-id="element-6402" data-mp="1mzgg1w4jxwt">
              <AwardIcon className="h-5 w-5 text-amber-500 mr-2" data-id="element-6403" data-mp="5pmb0ak6jwxp" />
              <h3 className={`text-xl font-semibold ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6404" data-mp="5nt1mni6mym0">
                Fun Facts
              </h3>
            </div>
            <ul className="space-y-2" data-id="element-6405" data-mp="3pcxl9fzeg3vn">
              {authorData.funFacts.map((fact, index) => <li key={index} className="flex items-start" data-id="element-6406" data-mp="3l99ev0mi7ebz" data-mpi={index}>
                  <span className="text-amber-500 mr-2" data-id="element-6407" data-mp="3gciz6eem2kr3">•</span>
                  <span className={dk.isDark ? 'text-gray-300' : 'text-gray-700'} data-id="element-6408" data-mp="3ge2ea1oj6pra">
                    {fact}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </div>;
};